/** Shared Auto base product priority (Brand Board + Price & Promotions). */

import { forEachMatchingInfo } from '../matchingUtils';

export const STICK_BASE_PRODUCT_PACK_SEP = '\x1e';

export const BASE_PRODUCT_AUTO = '__brand_board_auto_base__';

export const BASE_PRODUCT_AUTO_QUEUE_STORAGE_KEY = 'brand_board_base_product_auto_queue_v1';

export const AUTO_BASE_QUEUE_SKU_PREFIX = 'sku:';

export const DEFAULT_BASE_PRODUCT_AUTO_QUEUES = {
  sticks: ['Terea', 'Evo', 'EVO', 'Fiit'],
  devices: ['Iqos Iluma i One', 'Iqos Iluma one'],
};

const normalizeValue = (value) => (value || '').toString().trim().toLowerCase();

export function decodeStickBaseProductOption(v) {
  if (typeof v !== 'string') return { name: v || '', pack: null };
  const i = v.indexOf(STICK_BASE_PRODUCT_PACK_SEP);
  if (i === -1) return { name: v, pack: null };
  const name = v.slice(0, i).trim();
  const raw = v.slice(i + 1);
  if (raw === 'u') return { name, pack: null };
  const n = Number(raw);
  return { name, pack: Number.isFinite(n) && n >= 1 ? n : null };
}

export function formatStickBaseProductOptionLabel(v) {
  const { name, pack } = decodeStickBaseProductOption(v);
  if (!name) return v || '';
  if (pack == null) return name;
  const unitWord = pack === 1 ? 'unit' : 'units';
  return `${name} (${pack} ${unitWord})`;
}

export function formatBaseProductSelectLabel(opt, isStickRoute) {
  if (!opt) return '';
  if (
    isStickRoute &&
    typeof opt === 'string' &&
    opt.includes(STICK_BASE_PRODUCT_PACK_SEP)
  ) {
    return formatStickBaseProductOptionLabel(opt);
  }
  return String(opt);
}

export function readBaseProductAutoQueuesFromStorage() {
  try {
    const raw = localStorage.getItem(BASE_PRODUCT_AUTO_QUEUE_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_BASE_PRODUCT_AUTO_QUEUES };
    const parsed = JSON.parse(raw);
    return {
      sticks: Array.isArray(parsed.sticks)
        ? parsed.sticks.map(String)
        : [...DEFAULT_BASE_PRODUCT_AUTO_QUEUES.sticks],
      devices: Array.isArray(parsed.devices)
        ? parsed.devices.map(String)
        : [...DEFAULT_BASE_PRODUCT_AUTO_QUEUES.devices],
    };
  } catch {
    return { ...DEFAULT_BASE_PRODUCT_AUTO_QUEUES };
  }
}

export function persistBaseProductAutoQueues(queues) {
  try {
    localStorage.setItem(BASE_PRODUCT_AUTO_QUEUE_STORAGE_KEY, JSON.stringify(queues));
  } catch {
    /* ignore quota */
  }
}

function baseProductOptionDisplayName(opt, isStickRoute) {
  if (
    isStickRoute &&
    typeof opt === 'string' &&
    opt.includes(STICK_BASE_PRODUCT_PACK_SEP)
  ) {
    return decodeStickBaseProductOption(opt).name;
  }
  return String(opt || '');
}

function pickSmallestPackStickOption(matching) {
  if (!matching?.length) return null;
  const scored = matching.map((opt) => {
    const { pack } = decodeStickBaseProductOption(opt);
    return { opt, packNum: pack != null ? pack : Number.POSITIVE_INFINITY };
  });
  scored.sort((a, b) => a.packNum - b.packNum || String(a.opt).localeCompare(String(b.opt)));
  return scored[0].opt;
}

function selectOptionsForSkuTitle(sku, selectOptions, isStickRoute, groupNameToProducts) {
  const skuNorm = normalizeValue(sku);
  if (!skuNorm || !selectOptions?.length) return [];
  let groupName = null;
  if (groupNameToProducts) {
    groupNameToProducts.forEach((skus, name) => {
      if (groupName) return;
      if (skus.some((s) => normalizeValue(s) === skuNorm)) groupName = name;
    });
  }
  const matchKeys = new Set();
  if (groupName) matchKeys.add(normalizeValue(groupName));
  matchKeys.add(skuNorm);
  return selectOptions.filter((opt) => {
    const label = baseProductOptionDisplayName(opt, isStickRoute);
    const norm = normalizeValue(label);
    return matchKeys.has(norm) || norm === skuNorm;
  });
}

export function buildAutoBaseProductQueueChoices(
  selectOptions,
  skuTitles,
  isStickRoute,
  _groupNameToProducts
) {
  const choices = [];
  const seenValues = new Set();
  (selectOptions || []).forEach((opt) => {
    const value = String(opt);
    if (!value || seenValues.has(value)) return;
    seenValues.add(value);
    choices.push({
      value,
      label: formatBaseProductSelectLabel(opt, isStickRoute),
      kind: 'base_product',
    });
  });
  (skuTitles || []).forEach((title) => {
    const t = String(title || '').trim();
    if (!t) return;
    const value = `${AUTO_BASE_QUEUE_SKU_PREFIX}${t}`;
    if (seenValues.has(value)) return;
    seenValues.add(value);
    choices.push({
      value,
      label: t,
      kind: 'sku',
    });
  });
  choices.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'base_product' ? -1 : 1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
  return choices;
}

export function migrateLegacyQueueEntry(entry, choices, selectOptions, isStickRoute) {
  const raw = String(entry || '').trim();
  if (!raw) return '';
  if (choices.some((c) => c.value === raw)) return raw;
  if (selectOptions.some((o) => String(o) === raw)) return raw;
  if (raw.startsWith(AUTO_BASE_QUEUE_SKU_PREFIX)) {
    const sku = raw.slice(AUTO_BASE_QUEUE_SKU_PREFIX.length);
    const hit = choices.find(
      (c) => c.kind === 'sku' && normalizeValue(c.label) === normalizeValue(sku)
    );
    return hit ? hit.value : raw;
  }
  const term = normalizeValue(raw);
  const exactChoice = choices.find((c) => normalizeValue(c.label) === term);
  if (exactChoice) return exactChoice.value;
  const partialChoice = choices.find(
    (c) =>
      normalizeValue(c.label).includes(term) ||
      term.includes(normalizeValue(c.label))
  );
  if (partialChoice) return partialChoice.value;
  const skuChoice = choices.find(
    (c) => c.kind === 'sku' && normalizeValue(c.label).includes(term)
  );
  if (skuChoice) return skuChoice.value;
  const fuzzyOptions = selectOptions.filter((opt) => {
    const label = baseProductOptionDisplayName(opt, isStickRoute);
    const norm = normalizeValue(label);
    return norm === term || norm.includes(term);
  });
  if (fuzzyOptions.length === 1) return String(fuzzyOptions[0]);
  if (fuzzyOptions.length > 1 && isStickRoute) {
    const picked = pickSmallestPackStickOption(fuzzyOptions);
    if (picked) return String(picked);
  }
  if (fuzzyOptions[0]) return String(fuzzyOptions[0]);
  return raw;
}

/** Group names + ungrouped SKUs for a title list (same shape as base product select options). */
export function buildBaseProductSelectOptionsFromTitles(titles, matchingGroups) {
  const titleSet = new Set(titles || []);
  const groupNames = new Set();
  forEachMatchingInfo(matchingGroups, (info, title) => {
    if (titleSet.has(title) && info?.groupName) groupNames.add(info.groupName);
  });
  const productsInGroups = new Set();
  forEachMatchingInfo(matchingGroups, (info, title) => {
    if (info?.groupName) productsInGroups.add(title);
  });
  const ungroupedOnly = [...titleSet].filter((t) => !productsInGroups.has(t)).sort();
  return [...[...groupNames].sort(), ...ungroupedOnly];
}

/**
 * Resolve Auto queue independently per country from rows that exist in that market.
 * @param {{ queueEntries: string[], rows: {country?: string, title?: string}[], matchingGroups, groupNameToProducts, isStickRoute?: boolean }} params
 */
export function buildAutoBaseProductByCountryMap({
  queueEntries,
  rows,
  matchingGroups,
  groupNameToProducts,
  isStickRoute = false,
}) {
  const titlesByCountry = new Map();
  (rows || []).forEach((item) => {
    const c = item?.country;
    const t = item?.title;
    if (!c || !t) return;
    if (!titlesByCountry.has(c)) titlesByCountry.set(c, new Set());
    titlesByCountry.get(c).add(t);
  });
  const result = new Map();
  titlesByCountry.forEach((titleSet, country) => {
    const options = buildBaseProductSelectOptionsFromTitles([...titleSet], matchingGroups);
    const picked = pickBaseProductFromAutoQueue(
      queueEntries,
      options,
      isStickRoute,
      groupNameToProducts
    );
    if (picked) result.set(country, picked);
  });
  return result;
}

/** First queue entry that matches an option in the current filtered base product list. */
export function pickBaseProductFromAutoQueue(
  queueEntries,
  selectOptions,
  isStickRoute,
  groupNameToProducts
) {
  if (!queueEntries?.length || !selectOptions?.length) return null;
  for (const entry of queueEntries) {
    const raw = String(entry || '').trim();
    if (!raw) continue;

    if (raw.startsWith(AUTO_BASE_QUEUE_SKU_PREFIX)) {
      const sku = raw.slice(AUTO_BASE_QUEUE_SKU_PREFIX.length);
      const matching = selectOptionsForSkuTitle(sku, selectOptions, isStickRoute, groupNameToProducts);
      if (matching.length) {
        return isStickRoute ? pickSmallestPackStickOption(matching) : matching[0];
      }
      continue;
    }

    const exactOpt = selectOptions.find((opt) => String(opt) === raw);
    if (exactOpt) {
      if (isStickRoute && String(exactOpt).includes(STICK_BASE_PRODUCT_PACK_SEP)) {
        const name = baseProductOptionDisplayName(exactOpt, isStickRoute);
        const sameName = selectOptions.filter(
          (opt) =>
            normalizeValue(baseProductOptionDisplayName(opt, isStickRoute)) === normalizeValue(name)
        );
        return pickSmallestPackStickOption(sameName) || exactOpt;
      }
      return exactOpt;
    }

    const term = normalizeValue(raw);
    const matching = selectOptions.filter((opt) => {
      const norm = normalizeValue(baseProductOptionDisplayName(opt, isStickRoute));
      return norm === term || norm.includes(term);
    });
    if (!matching.length) continue;
    if (isStickRoute && matching.some((m) => String(m).includes(STICK_BASE_PRODUCT_PACK_SEP))) {
      const picked = pickSmallestPackStickOption(matching);
      if (picked) return picked;
    }
    return matching[0];
  }
  return null;
}
